import { bindActionCreators } from "@reduxjs/toolkit";
import { type FC, useEffect } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { SearchInput } from "../../../../../../../components";
import Restricted from "../../../../../../Application/Restricted";
import GroupsListContainer from "./GroupsListContainer/GroupsListContainer";

import ContentGroupsNoResults from "features/Library/Common/Fields/ContentGroupsNoResults";
import { AssignmentPeopleContext, RolePermissions, SortingDirection } from "../../../../../../../enums";
import { useContentAssignmentsPermissions } from "../../../../../../../hooks/useContentAssignmentsPermissions";
import { type FiltersMap } from "../../../../../../../utils/filterUtils";
import { type AppDispatch, type RootState } from "../../../../../../Application/globaltypes/redux";
import { DEFAULT_REQUEST_PARAMS } from "../../../../../PeopleAssignments/Overview/helpers/constants";
import {
  ColumnToParamMap,
  PeopleAssignmentColumns,
  getGroupColumnOptions,
} from "../../../../../PeopleAssignments/Overview/helpers/getColumnOptions";
import { resetSelectedItems } from "../../../../state/slices/eventGroupsSlice";
import { fetchEventGroups } from "../../../../state/thunks/eventGroupsThunk";

export interface GroupsOverviewPropsBase {
  eventId: number;
  customHeaderContent: React.ReactElement;
}

export type GroupsOverviewProps = GroupsOverviewPropsBase & PropsFromRedux;

export const GroupsOverview: FC<GroupsOverviewProps> = ({ eventId, customHeaderContent, fetchGroups, onUnmount }) => {
  const renderSearchInput = (): React.ReactElement => <SearchInput placeholder="Search for Groups..." disabled />;

  useEffect(
    () => () => {
      onUnmount();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getEventGroups = (
    skip: number = DEFAULT_REQUEST_PARAMS.skip,
    top: number = DEFAULT_REQUEST_PARAMS.top,
    orderBy: string = PeopleAssignmentColumns.Name,
    sortingDirection: SortingDirection = DEFAULT_REQUEST_PARAMS.sortingDirection,
    appliedFilter: FiltersMap = DEFAULT_REQUEST_PARAMS.appliedFilter,
  ): void => {
    const sortingColumn = ColumnToParamMap[orderBy.toLowerCase() as Lowercase<PeopleAssignmentColumns>];
    fetchGroups({
      entityId: eventId,
      skip,
      top,
      sortingColumn,
      sortingDirection,
      filter: appliedFilter,
      term: "",
    });
  };

  const permissionPredicate = useContentAssignmentsPermissions(
    AssignmentPeopleContext.Group,
    RolePermissions.EventsManage,
  );

  return (
    <Restricted
      permissions={[]}
      permissionPredicate={permissionPredicate}
      renderContent={() => (
        <GroupsListContainer
          defaultSortingColumnName={""}
          sortingDirection={SortingDirection.Ascending}
          columnOptions={getGroupColumnOptions()}
          applyFilter={/* istanbul ignore next */ () => {}} // NOSONAR
          resetFilter={/* istanbul ignore next */ () => {}} // NOSONAR
          fetchData={getEventGroups}
          renderSearch={renderSearchInput}
          customHeaderContent={customHeaderContent}
          isSelectDisabled={() => true}
          noResultsContent={<ContentGroupsNoResults />}
        />
      )}
    />
  );
};
/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchGroups: bindActionCreators(fetchEventGroups, dispatch),
  onUnmount: () => dispatch(resetSelectedItems()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(GroupsOverview);
export default ConnectedComponent;
